import m, {MithrilComponent} from '@bitstillery/mithril'
import {classes, plausible_event, present, ucfirst} from '@bitstillery/common/lib/utils'
import {
    Button,
    ButtonGroup,
    FieldCheckbox,
    FieldSelect,
    FieldText,
    FieldTextArea,
    RadioGroup,
    Stepper,
} from '@bitstillery/common/components/portal'
import {countries, european_union_countries} from '@bitstillery/common/lib/countries'
import {state} from '@bitstillery/mithril'
import {and, conditional, email, required, validation, website} from '@bitstillery/common/lib/validation'
import {$t, api, notifier} from '@bitstillery/common/app'

const country_options = Object.entries(countries)

export interface AuthRegistrationAttrs {
    /** Called when the current step changes; (step, image_text) for dialog layout coordination */
    on_step_change?: (step: number, image_text: string | {quote: string; cite: string}) => void
}

export class AuthRegistration extends MithrilComponent<AuthRegistrationAttrs> {
    data = (() => {
        const form = {
            address: '',
            city: '',
            company_name: '',
            consent: false,
            country_code: '',
            emailaddress: '',
            excise_id: '',
            excise_location_id: '',
            family_name: '',
            first_name: '',
            has_warehouse: '',
            message: '',
            postal_code: '',
            position: '',
            telephone: '',
            terms_and_conditions: false,
            vat_id: '',
            warehouse: '', // A known third party bonded warehouse
            warehouse_address: '',
            warehouse_city: '',
            warehouse_name: '', // An unknown third party bonded warehouse
            warehouse_postalcode: '',
            website: '',
        }

        const disabled_group = (step: number) => () => {
            if (!this.validationGroup[step]) {
                return false
            }
            const validation_fields = this.validationGroup[step]
            const invalid = validation_fields.map((i) => this.$v[i]).filter((i) => i._invalid)
            return invalid.length > 0
        }

        return state(
            {
                form,
                stepper: {
                    // `completed` is set as each step is submitted.
                    options: [
                        {completed: false, icon: 'company', title: $t('registration.step0_slug')},
                        {_disabled: disabled_group(0), completed: false, icon: 'address', title: $t('registration.step1_slug')},
                        {_disabled: disabled_group(1), completed: false, icon: 'contact', title: $t('registration.step2_slug')},
                        {_disabled: disabled_group(2), completed: false, icon: 'logistics', title: $t('registration.step3_slug')},
                        {
                            _disabled: disabled_group(3),
                            completed: false,
                            icon: 'clipboardText',
                            title: $t('registration.step4_slug'),
                        },
                    ],
                    selection: 0,
                },
            },
            'registration-data',
        )
    })()

    $v = {
        address: validation(this.data.form.$address, required()),
        city: validation(this.data.form.$city, required()),
        company_name: validation(this.data.form.$company_name, required()),
        consent: validation(this.data.form.$consent, required()),
        country_code: validation(this.data.form.$country_code, required()),
        emailaddress: validation(this.data.form.$emailaddress, and([required(), email()])),
        excise_id: validation(
            this.data.form.$excise_id,
            conditional(() => this.data.form.has_warehouse === 'own_bonded', required()),
        ),
        excise_location_id: validation(
            this.data.form.$excise_location_id,
            conditional(() => this.data.form.has_warehouse === 'own_bonded', required()),
        ),
        family_name: validation(this.data.form.$family_name, required()),
        first_name: validation(this.data.form.$first_name, required()),
        has_warehouse: validation(this.data.form.$has_warehouse, required()),
        postal_code: validation(this.data.form.$postal_code, required()),
        telephone: validation(this.data.form.$telephone, required()),
        terms_and_conditions: validation(this.data.form.$terms_and_conditions, required()),
        vat_id: validation(
            this.data.form.$vat_id,
            conditional(() => european_union_countries.includes(this.data.form.country_code), required()),
        ),
        warehouse_address: validation(
            this.data.form.$warehouse_address,
            conditional(
                () =>
                    ['vat_excise', 'own_bonded'].includes(this.data.form.has_warehouse) ||
                    (this.data.form.has_warehouse === 'third_party_bonded' &&
                        (!this.data.form.warehouse || this.data.form.warehouse === 'OTHER')),
                required(),
            ),
        ),
        warehouse_city: validation(
            this.data.form.$warehouse_city,
            conditional(
                () =>
                    ['vat_excise', 'own_bonded'].includes(this.data.form.has_warehouse) ||
                    (this.data.form.has_warehouse === 'third_party_bonded' &&
                        (!this.data.form.warehouse || this.data.form.warehouse === 'OTHER')),
                required(),
            ),
        ),
        warehouse_name: validation(
            this.data.form.$warehouse_name,
            conditional(
                () =>
                    this.data.form.has_warehouse === 'own_bonded' ||
                    (this.data.form.has_warehouse === 'third_party_bonded' &&
                        (!this.data.form.warehouse || this.data.form.warehouse === 'OTHER')),
                required(),
            ),
        ),
        warehouse_postalcode: validation(
            this.data.form.$warehouse_postalcode,
            conditional(
                () =>
                    ['vat_excise', 'own_bonded'].includes(this.data.form.has_warehouse) ||
                    (this.data.form.has_warehouse === 'third_party_bonded' &&
                        (!this.data.form.warehouse || this.data.form.warehouse === 'OTHER')),
                required(),
            ),
        ),
        website: validation(this.data.form.$website, website()),
    }

    last_step = this.data.stepper.options.length - 1
    terms_link =
        process.env.MSI_THEME === 'msp'
            ? 'https://movingspirits.eu/terms-and-conditions/'
            : 'https://a2bc.com/general-terms-and-conditions-page/'
    // Steps without fields have no group.
    validationGroup: Record<number, (keyof AuthRegistration['$v'])[] | undefined> = {
        0: ['company_name', 'website'],
        1: ['country_code', 'address', 'postal_code', 'city'],
        2: ['first_name', 'family_name', 'emailaddress', 'telephone'],
        3: [
            'has_warehouse',
            'vat_id',
            'warehouse_name',
            'warehouse_address',
            'warehouse_postalcode',
            'warehouse_city',
            'warehouse_name',
            'excise_id',
            'excise_location_id',
        ],
        4: ['consent', 'terms_and_conditions'],
    }

    // Looked up by the form's free `warehouse` value.
    warehouses: Record<string, string> = {
        LOENDERSLOOT: 'Loendersloot (Roosendaal, Netherlands)',
        OTHER: $t('registration.warehouse.options.other'),
        SEABROOKS: 'Seabrooks Warehousing (London, UK)',
        TOP: 'Top Logistics (Spijkenisse, Netherlands)',
    }

    override oninit(vnode?: m.Vnode<AuthRegistrationAttrs>) {
        this._last_reported_step = this.data.stepper.selection
        this._report_step_change(vnode, this.data.stepper.selection)

        // Try to auto-determine the current country based on browser language
        let country_code = ''
        if (navigator.language.includes('-')) {
            country_code = present(navigator.language.split('-')[1], 'region subtag').toUpperCase()
        } else {
            const language_code = navigator.language.toUpperCase()
            if (countries[language_code]) {
                country_code = language_code
            }
        }

        this.data.form.country_code = country_code

        this.plausible_event(this.data.stepper.selection)
    }

    _last_reported_step: number | null = null

    _report_step_change(vnode: m.Vnode<AuthRegistrationAttrs> | undefined, step: number) {
        if (!vnode?.attrs.on_step_change || this._last_reported_step === step) return
        this._last_reported_step = step
        const vat_required = european_union_countries.includes(this.data.form.country_code)
        const text =
            step === 0
                ? {quote: $t('testimonial.quote'), cite: $t('testimonial.cite')}
                : step === 1
                  ? $t('registration.step1_desc')
                  : step === 2
                    ? $t('registration.step2_desc')
                    : step === 3
                      ? $t(`registration.step3_desc${vat_required ? '_vat' : ''}`)
                      : step === 4
                        ? $t('registration.step4_desc')
                        : step === 5
                          ? $t('registration.step5_desc')
                          : ''
        vnode.attrs.on_step_change(step, text)
    }

    override onupdate(vnode?: m.Vnode<AuthRegistrationAttrs>) {
        this._report_step_change(vnode, this.data.stepper.selection)
    }

    plausible_event(step: number) {
        // Track step progression
        const step_names = [
            'registration_request_company_info',
            'registration_request_address',
            'registration_request_contact',
            'registration_request_logistics',
            'registration_request_terms',
            'registration_request_completed',
        ]
        plausible_event('registration_request', {
            props: {
                country: this.data.form.country_code,
                step: step_names[step],
            },
        })
    }

    async submit_form() {
        const data = {
            account_slug: process.env.MSI_THEME,
            city: this.data.form.city,
            company_name: this.data.form.company_name,
            country_code: this.data.form.country_code,
            emailaddress: this.data.form.emailaddress,
            family_name: this.data.form.family_name,
            first_name: this.data.form.first_name,
            message: this.data.form.message,
            position: this.data.form.position,
            street_address: this.data.form.address,
            telephone: this.data.form.telephone,
            vat_id: this.data.form.vat_id,
            website: this.data.form.website,
            zip_code: this.data.form.postal_code,
        }

        // New LeadWarehouse is created when the warehouse_name property is set.
        if (this.data.form.has_warehouse !== 'no') {
            Object.assign(data, {
                excise_id: this.data.form.excise_id,
                excise_location_id: this.data.form.excise_location_id,
                warehouse_address: this.data.form.warehouse_address,
                warehouse_city: this.data.form.warehouse_city,
                warehouse_name: this.data.form.warehouse_name,
                warehouse_postalcode: this.data.form.warehouse_postalcode,
            })
        }

        await api.post('portal/create_registration', data, true)
        notifier.notify($t('registration.step5_notify'), 'info')
        this.data.stepper.selection += 1
        this.plausible_event(this.data.stepper.selection)
    }

    view() {
        const vat_required = european_union_countries.includes(this.data.form.country_code)

        return (
            <div className={classes('c-registration', 'landing', 'c-registration--in-dialog')}>
                {this.data.stepper.selection < this.data.stepper.options.length && (
                    <Stepper model={this.data.stepper} options={this.data.stepper.options} />
                )}
                <div className='form stepper-form'>
                    {this.data.stepper.selection === 0 && (
                        <div className='step step-0'>
                            <h3>{$t('registration.step0_slug')}</h3>
                            <p className='step-info'>{$t('registration.step0_desc', {vendor: process.env.MSI_TITLE})}</p>

                            <FieldText
                                label={$t('registration.company_name')}
                                model={this.data.form.$company_name}
                                placeholder={$t('registration.company_name_placeholder')}
                                validation={this.$v.company_name}
                            />

                            <FieldText
                                label={$t('registration.website')}
                                model={this.data.form.$website}
                                placeholder={$t('registration.website_placeholder')}
                                validation={this.$v.website}
                            />
                        </div>
                    )}

                    {this.data.stepper.selection === 1 && (
                        <div className='step step-1'>
                            <h3>{$t('registration.step1_slug')}</h3>
                            <p className='step-info'>{$t('registration.step1_desc')}</p>

                            <FieldSelect
                                help={$t('registration.country_help')}
                                label={$t('registration.country')}
                                model={this.data.form.$country_code}
                                options={country_options.map(([value, label]) => ({label, value}))}
                                validation={this.$v.country_code}
                            />

                            <FieldText
                                label={$t('registration.address')}
                                model={this.data.form.$address}
                                placeholder={$t('registration.address_placeholder')}
                                validation={this.$v.address}
                            />

                            <div className='field-group'>
                                <FieldText
                                    label={$t('registration.city')}
                                    model={this.data.form.$city}
                                    placeholder={$t('registration.city_placeholder')}
                                    validation={this.$v.city}
                                />
                                <FieldText
                                    label={$t('registration.postal_code')}
                                    model={this.data.form.$postal_code}
                                    placeholder={$t('registration.postal_code_placeholder')}
                                    validation={this.$v.postal_code}
                                />
                            </div>
                        </div>
                    )}

                    {this.data.stepper.selection === 2 && (
                        <div className='step step-2'>
                            <h3>{$t('registration.step2_slug')}</h3>
                            <p className='step-info'>{$t('registration.step2_desc')}</p>
                            <div className='field-group'>
                                <FieldText
                                    label={$t('registration.first_name')}
                                    model={this.data.form.$first_name}
                                    placeholder={$t('registration.first_name_placeholder')}
                                    validation={this.$v.first_name}
                                />
                                <FieldText
                                    label={$t('registration.family_name')}
                                    model={this.data.form.$family_name}
                                    placeholder={$t('registration.family_name_placeholder')}
                                    validation={this.$v.family_name}
                                />
                            </div>

                            <FieldText
                                label={$t('registration.email_address')}
                                help={$t('registration.email_address_help')}
                                model={this.data.form.$emailaddress}
                                placeholder={$t('registration.email_address')}
                                validation={this.$v.emailaddress}
                            />

                            <FieldText
                                label={$t('registration.telephone')}
                                model={this.data.form.$telephone}
                                placeholder={$t('registration.telephone')}
                                validation={this.$v.telephone}
                            />

                            <FieldText
                                label={$t('registration.position')}
                                model={this.data.form.$position}
                                placeholder={$t('registration.position_placeholder')}
                            />
                        </div>
                    )}

                    {this.data.stepper.selection === 3 && (
                        <div className='step step-3'>
                            <h3>{$t('registration.step3_slug')}</h3>
                            <p className='step-info'>{$t(`registration.step3_desc${vat_required ? '_vat' : ''}`)}</p>
                            {(() => {
                                const bonded_warehouse = [
                                    <RadioGroup
                                        label={$t('registration.warehouse.select')}
                                        model={this.data.form.$warehouse}
                                        options={Object.entries(this.warehouses)}
                                    />,
                                    this.data.form.warehouse === 'OTHER'
                                        ? [
                                              <div className='field-group'>
                                                  <FieldText
                                                      label={$t('registration.warehouse.name')}
                                                      model={this.data.form.$warehouse_name}
                                                      placeholder={$t('registration.warehouse.name')}
                                                      validation={this.$v.warehouse_name}
                                                  />

                                                  <FieldText
                                                      label={$t('registration.warehouse.address')}
                                                      model={this.data.form.$warehouse_address}
                                                      placeholder={$t('registration.warehouse.address')}
                                                      validation={this.$v.warehouse_address}
                                                  />
                                              </div>,
                                              <div className='field-group'>
                                                  <FieldText
                                                      label={$t('registration.warehouse.postal_code')}
                                                      model={this.data.form.$warehouse_postalcode}
                                                      placeholder={$t('registration.warehouse.postal_code')}
                                                      validation={this.$v.warehouse_postalcode}
                                                  />
                                                  <FieldText
                                                      label={$t('registration.warehouse.city')}
                                                      model={this.data.form.$warehouse_city}
                                                      placeholder={$t('registration.warehouse.city')}
                                                      validation={this.$v.warehouse_city}
                                                  />
                                              </div>,
                                          ]
                                        : null,
                                ]

                                if (vat_required) {
                                    return [
                                        <FieldText
                                            help={$t('registration.vat_number_help')}
                                            label={$t('registration.vat_number')}
                                            model={this.data.form.$vat_id}
                                            placeholder={$t('registration.vat_number_placeholder')}
                                            validation={this.$v.vat_id}
                                        />,

                                        <RadioGroup
                                            label={$t('registration.warehouse.warehousing')}
                                            model={this.data.form.$has_warehouse}
                                            options={this.warehouse_options()}
                                        />,

                                        this.data.form.has_warehouse === 'vat_excise'
                                            ? [
                                                  <FieldText
                                                      help={$t('registration.warehouse.address_unloading_help')}
                                                      label={$t('registration.warehouse.address_unloading')}
                                                      model={this.data.form.$warehouse_address}
                                                      placeholder={$t('registration.warehouse.address_unloading')}
                                                      validation={this.$v.warehouse_address}
                                                  />,
                                                  <div className='field-group'>
                                                      <FieldText
                                                          label={$t('registration.warehouse.postal_code_unloading')}
                                                          model={this.data.form.$warehouse_postalcode}
                                                          placeholder={$t('registration.warehouse.postal_code_unloading')}
                                                          validation={this.$v.warehouse_postalcode}
                                                      />
                                                      <FieldText
                                                          label={$t('registration.warehouse.city_unloading')}
                                                          placeholder={$t('registration.warehouse.city_unloading')}
                                                          model={this.data.form.$warehouse_city}
                                                          validation={this.$v.warehouse_city}
                                                      />
                                                  </div>,
                                              ]
                                            : null,

                                        this.data.form.has_warehouse === 'own_bonded'
                                            ? [
                                                  <div className='field-group'>
                                                      <FieldText
                                                          label={$t('registration.warehouse.name')}
                                                          model={this.data.form.$warehouse_name}
                                                          placeholder={$t('registration.warehouse.name')}
                                                          validation={this.$v.warehouse_name}
                                                      />
                                                      <FieldText
                                                          help={$t('registration.warehouse.address_help')}
                                                          label={$t('registration.warehouse.address')}
                                                          model={this.data.form.$warehouse_address}
                                                          placeholder={$t('registration.warehouse.address')}
                                                          validation={this.$v.warehouse_address}
                                                      />
                                                  </div>,

                                                  <div className='field-group'>
                                                      <FieldText
                                                          label={$t('registration.warehouse.postal_code')}
                                                          model={this.data.form.$warehouse_postalcode}
                                                          placeholder={$t('registration.warehouse.postal_code')}
                                                          validation={this.$v.warehouse_postalcode}
                                                      />

                                                      <FieldText
                                                          label={$t('registration.warehouse.city')}
                                                          model={this.data.form.$warehouse_city}
                                                          placeholder={$t('registration.warehouse.city')}
                                                          validation={this.$v.warehouse_city}
                                                      />
                                                  </div>,
                                                  <div className='field-group'>
                                                      <FieldText
                                                          label={$t('registration.warehouse.excise_id')}
                                                          model={this.data.form.$excise_id}
                                                          placeholder={$t('registration.warehouse.excise_id')}
                                                          validation={this.$v.excise_id}
                                                      />
                                                      <FieldText
                                                          label={`${$t('registration.warehouse.location_id')} ${this.data.form.country_code === 'NL' ? '(AGP)' : ''}`}
                                                          model={this.data.form.$excise_location_id}
                                                          placeholder={$t('registration.warehouse.location_id')}
                                                          validation={this.$v.excise_location_id}
                                                      />
                                                  </div>,
                                              ]
                                            : null,
                                        this.data.form.has_warehouse === 'third_party_bonded' ? bonded_warehouse : null,
                                    ]
                                }
                                return [
                                    <RadioGroup
                                        label={$t('registration.warehouse.bonded_eu')}
                                        model={this.data.form.$has_warehouse}
                                        options={[
                                            ['third_party_bonded', $t('yes')],
                                            ['no', $t('no')],
                                        ]}
                                    />,
                                    this.data.form.has_warehouse === 'third_party_bonded' ? bonded_warehouse : null,
                                ]
                            })()}
                        </div>
                    )}

                    {this.data.stepper.selection === 4 && (
                        <div className='step step-4'>
                            <h3>{$t('registration.step4_slug')}</h3>
                            <p className='step-info'>{$t('registration.step4_desc')}</p>

                            <div className='field-readonly-list'>
                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.company_name')}</div>
                                    <div className='value'>{this.data.form.company_name ? this.data.form.company_name : '-'}</div>
                                </div>
                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.website')}</div>
                                    <div className='value'>{this.data.form.website ? this.data.form.website : '-'}</div>
                                </div>

                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.postal_code')}</div>
                                    <div className='value'>{this.data.form.postal_code ? this.data.form.postal_code : '-'}</div>
                                </div>
                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.address')}</div>
                                    <div className='value'>{this.data.form.address ? this.data.form.address : '-'}</div>
                                </div>

                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.city')}</div>
                                    <div className='value'>{this.data.form.city ? this.data.form.city : '-'}</div>
                                </div>
                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.country')}</div>
                                    <div className='value'>{countries[this.data.form.country_code]}</div>
                                </div>

                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.name')}</div>
                                    <div className='value'>
                                        {!this.data.form.first_name && !this.data.form.family_name
                                            ? '-'
                                            : `${this.data.form.first_name} ${this.data.form.family_name}`}
                                    </div>
                                </div>

                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.position')}</div>
                                    <div className='value'>{this.data.form.position ? this.data.form.position : '-'}</div>
                                </div>

                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.telephone')}</div>
                                    <div className='value'>{this.data.form.telephone ? this.data.form.telephone : '-'}</div>
                                </div>

                                <div className='field-readonly'>
                                    <div className='key'>{$t('registration.email_address')}</div>
                                    <div className='value'>{this.data.form.emailaddress ? this.data.form.emailaddress : '-'}</div>
                                </div>

                                {vat_required && (
                                    <div className='field-readonly'>
                                        <div className='key'>{$t('registration.vat_number')}</div>
                                        <div className='value'>{this.data.form.vat_id ? this.data.form.vat_id : '-'}</div>
                                    </div>
                                )}

                                <div className='field-readonly'>
                                    <div className='key'>
                                        {this.data.form.has_warehouse === 'vat_excise'
                                            ? $t('registration.warehouse.name_unloading')
                                            : $t('registration.warehouse.name')}
                                    </div>
                                    <div className='value'>
                                        {(() => {
                                            if (this.data.form.warehouse === 'OTHER') {
                                                return `${ucfirst(this.data.form.warehouse_name)}${this.data.form.warehouse_address ? ` (${this.data.form.warehouse_address})` : ''}`
                                            } else if (['own_bonded', 'vat_excise'].includes(this.data.form.has_warehouse)) {
                                                return `${this.data.form.warehouse_name} (${this.data.form.warehouse_address}, ${this.data.form.postal_code}, ${this.data.form.warehouse_city})`
                                            } else if (this.data.form.has_warehouse === 'temporary_license') {
                                                return $t('registration.import_goods_temp_license')
                                            } else if (Object.keys(this.data.form.warehouse).length > 0) {
                                                return this.warehouses[this.data.form.warehouse.toUpperCase()]
                                            }

                                            return '-'
                                        })()}
                                    </div>
                                </div>

                                {this.data.form.excise_id && (
                                    <div className='field-readonly'>
                                        <div className='key'>{$t('registration.warehouse.excise_id')}</div>
                                        <div className='value'>{this.data.form.excise_id}</div>
                                    </div>
                                )}

                                {this.data.form.excise_location_id && (
                                    <div className='field-readonly'>
                                        <div className='key'>{$t('registration.warehouse.location_id')}</div>
                                        <div className='value'>{this.data.form.excise_location_id}</div>
                                    </div>
                                )}
                            </div>

                            <FieldTextArea
                                placeholder={$t('registration.message_placeholder')}
                                label={$t('registration.message')}
                                model={this.data.form.$message}
                            />

                            <div className='consent'>
                                <FieldCheckbox
                                    help={$t('registration.consent_help')}
                                    label={$t('registration.consent', {vendor: process.env.MSI_TITLE})}
                                    model={this.data.form.$consent}
                                />
                                <FieldCheckbox
                                    help={$t('registration.agree_help')}
                                    label={m.trust(
                                        $t('registration.agree', {interpolation: {escapeValue: false}, link: this.terms_link}),
                                    )}
                                    model={this.data.form.$terms_and_conditions}
                                />
                            </div>
                        </div>
                    )}

                    {this.data.stepper.selection === 5 && (
                        <div className='step step-5'>
                            <div className='team-regards'>
                                <img className='team' src='/common/img/team.jpg' />
                                <span>{$t(`support.contact.team_${process.env.MSI_THEME}`)}</span>
                            </div>
                            <h2>{$t('registration.step5_title')}</h2>
                            <p className='mt-05'>{$t('registration.step5_desc')}</p>
                            <p className='mt-1'>
                                {$t('registration.step5_footer')}
                                {/** Reference to Youtube channel (MSI only) */}
                                {process.env.MSI_THEME === 'msp' && m.trust(`&nbsp${$t('registration.step5_incentive')}`)}
                            </p>
                        </div>
                    )}

                    {this.data.stepper.selection <= this.last_step && (
                        <ButtonGroup>
                            {this.data.stepper.selection > 0 && (
                                <Button
                                    icon='chevronLeft'
                                    text={$t('back')}
                                    type='default'
                                    onclick={() => {
                                        this.data.stepper.selection -= 1
                                        this.plausible_event(this.data.stepper.selection)
                                    }}
                                />
                            )}

                            <Button
                                disabled={(() => {
                                    const validation_fields = this.validationGroup[this.data.stepper.selection]
                                    if (!validation_fields) {
                                        return false
                                    }
                                    const invalid = validation_fields.map((i) => this.$v[i]).filter((i) => i._invalid)
                                    return invalid.length
                                })()}
                                icon={this.data.stepper.selection === this.last_step ? 'checked' : 'chevronRight'}
                                // @i18n ignore: registration.step*_slug
                                text={(() => $t(`registration.step${this.data.stepper.selection + 1}_slug`))()}
                                type='info'
                                onclick={() => {
                                    // At this point the form is submitted. After registration, the next step is toggled.
                                    if (this.data.stepper.selection < this.last_step) {
                                        present(
                                            this.data.stepper.options[this.data.stepper.selection],
                                            'registration step',
                                        ).completed = true
                                        this.data.stepper.selection += 1

                                        this.plausible_event(this.data.stepper.selection)

                                        if (this.data.stepper.selection === 2) {
                                            // Prefill warehouse_name from company_name
                                            this.data.form.warehouse_name = this.data.form.company_name
                                        }
                                    } else if (this.data.stepper.selection === this.last_step) {
                                        void this.submit_form()
                                    }
                                }}
                            />
                        </ButtonGroup>
                    )}
                </div>
            </div>
        )
    }

    warehouse_options() {
        const options = [
            ['own_bonded', $t('registration.warehouse.options.bonded')],
            ['third_party_bonded', $t('registration.warehouse.options.third_party')],
            ['temporary_license', $t('registration.warehouse.options.temp_license')],
        ]
        if (this.data.form.country_code === 'NL') {
            options.unshift(['vat_excise', $t('registration.warehouse.options.inc_vat')])
        }
        return options
    }
}
